import type { TokenChangeEvent, TokenChangeListener, TokenStore } from './tokenStore';

/**
 * Источник изолированного входа вкладки («от имени» пользователя у поддержки): токен живёт только в хранилище этой
 * вкладки и не смешивается с обычным входом (localStorage, refresh-cookie и канал вкладок принадлежат сотруднику —
 * его собственный вход на том же адресе). Сам источник (билет в URL, ключи sessionStorage, открытие и продление на
 * сервере) — у приложения; рантайм только читает и меняет токен через этот порт.
 */
export interface AuthIsolatedSession {
    /**
     * Вкладка изолированного входа. Читается при каждом обращении: решение приложение принимает при загрузке страницы
     * и дальше не меняет, а тесты подменяют его по месту.
     */
    active(): boolean;
    getAccessToken(): string | null;
    /** Новый access — только в сеанс вкладки (нет сеанса — no-op). */
    setAccessToken(accessToken: string): void;
    /** Выход: чистится только сеанс вкладки, обычный вход сотрудника не трогается. */
    clear(): void;
    /** Пульс присутствия «не на экране» в фоне, мс: по нему сервер держит вход открытым. Без него фон молчит. */
    hiddenBeatIntervalMs?: number;
}

/**
 * Хранилище токенов поверх изолированного сеанса: режим всегда `body`, refresh-токена нет, запись из соседних вкладок
 * (`broadcast`) не принимается, рассылки нет — у вкладки нет канала.
 */
export function createIsolatedTokenStore(session: AuthIsolatedSession): TokenStore {
    const listeners = new Set<TokenChangeListener>();
    let generation = 0;

    const notify = (event: TokenChangeEvent) => {
        listeners.forEach((listener) => {
            try {
                listener(event);
            } catch {
                // упавший подписчик не мешает остальным
            }
        });
    };

    const clearTokens: TokenStore['clearTokens'] = (origin) => {
        if (origin === 'broadcast') return;
        generation += 1;
        session.clear();
        notify({ accessToken: null, origin });
    };

    return {
        getMode: () => 'body',
        setMode: () => undefined,
        getAccessToken: () => session.getAccessToken(),
        getRefreshToken: () => null,
        getTokens: () => ({ accessToken: session.getAccessToken(), refreshToken: null }),
        setTokens(tokens, origin) {
            if (origin === 'broadcast') return;
            if (!tokens.accessToken) {
                clearTokens('logout');
                return;
            }
            session.setAccessToken(tokens.accessToken);
            notify({ accessToken: session.getAccessToken(), origin });
        },
        clearTokens,
        epoch: () => generation,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
